import * as api from '@apis/commission-rates';
import { DEFAULT_PARTNER_CHANNEL_COMMISSIONREATE } from '@consts/user';
import { CommissionRateTemplate } from '@models/commission-rates';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation } from '@tanstack/react-query';
import { Button, Input, Modal } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useEffect, useState } from 'react';

export interface CommissionRateTemplateCreateModalProps {
  data?: CommissionRateTemplate;
  open?: boolean;
  onClose?: () => void;
  needRefetch?: () => void;
}

export const CommissionRateTemplateCreateModal: React.FC<CommissionRateTemplateCreateModalProps> = (props) => {
  const [createCommissionRateTemplateData, setCreateCommissionRateTemplateData] = useState<CommissionRateTemplate>({
    channelCommissionRate: {
      app: { ratePercent: DEFAULT_PARTNER_CHANNEL_COMMISSIONREATE['app'] },
      'naver-store': { ratePercent: DEFAULT_PARTNER_CHANNEL_COMMISSIONREATE['naver-store'] },
      'kakao-reservation': { ratePercent: DEFAULT_PARTNER_CHANNEL_COMMISSIONREATE['kakao-reservation'] },
      benepia: { ratePercent: DEFAULT_PARTNER_CHANNEL_COMMISSIONREATE['benepia'] },
    },
  });
  const isEditMode = !!props.data;
  const dialog = useOkCancelDialog();

  useEffect(() => {
    if (props.data) {
      setCreateCommissionRateTemplateData(props.data);
    }
  }, [props.data]);

  const createCommissionRateTemplateMutation = useMutation({
    mutationFn: api.createCommissionRateTemplate,
    onSuccess: async () => {
      dialog.open({
        content: '생성되었습니다.',
        type: 'ok',
        onConfirm: () => {
          props.onClose && props.onClose();
        },
      });
      props.needRefetch && props.needRefetch();
    },
    onError: async (e: any) => {
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message,
        type: 'ok',
      });
    },
  });

  const updateCommissionRateTemplateMutation = useMutation({
    mutationFn: api.updateCommissionRateTemplate,
    onSuccess: async () => {
      props.needRefetch && props.needRefetch();
      props.onClose && props.onClose();
      dialog.open({
        content: '수정했습니다.',
        type: 'ok',
      });
    },
    onError: async (e: any) => {
      props.needRefetch && props.needRefetch();
      props.onClose && props.onClose();
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message,
        type: 'ok',
      });
    },
  });

  if (!props.open) {
    return <></>;
  }

  return (
    <Modal
      styles={{ footer: { height: '44px' } }}
      open={props.open}
      destroyOnClose
      footer={
        <div style={{ width: '100%' }}>
          <Button
            style={{
              width: 120,
            }}
            onClick={props.onClose}>
            취소
          </Button>
          <Button
            style={{
              width: 120,
              marginLeft: '8px',
            }}
            onClick={() => {
              if (createCommissionRateTemplateData) {
                if (isEditMode) {
                  updateCommissionRateTemplateMutation.mutate({
                    templateId: createCommissionRateTemplateData._id!,
                    body: createCommissionRateTemplateData,
                  });
                } else {
                  createCommissionRateTemplateMutation.mutate(createCommissionRateTemplateData);
                }
              }
            }}>
            {isEditMode ? '수정하기' : '추가하기'}
          </Button>
        </div>
      }
      onCancel={props.onClose}>
      <OverlayScrollbarsComponent
        defer
        options={{ scrollbars: { autoHide: 'scroll' } }}
        style={{
          backgroundColor: '#ffffff',
          marginTop: 0,
          position: 'relative',
          height: 'calc(100dvh - 240px)',
          overflow: 'auto',
        }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: 10,
            }}>
            {isEditMode ? '수수료율 템플릿 편집' : '수수료율 템플릿 생성'}
          </div>
          <div style={{ textAlign: 'left', padding: 50 }}>
            <div style={{ marginTop: 10 }}>
              템플릿 이름
              <Input
                placeholder="템플릿 이름을 입력하세요"
                value={createCommissionRateTemplateData?.title}
                onChange={(e) => {
                  setCreateCommissionRateTemplateData((prev) => {
                    return { ...prev, title: e.target.value } as CommissionRateTemplate;
                  });
                }}
              />
            </div>
            <div style={{ marginTop: 10 }}>
              메모
              <Input
                value={createCommissionRateTemplateData?.memo}
                placeholder="메모"
                onChange={(e) => {
                  setCreateCommissionRateTemplateData((prev) => {
                    return {
                      ...prev,
                      memo: e.target.value,
                    } as CommissionRateTemplate;
                  });
                }}
              />
            </div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: '10px' }}>
              수수료율(숫자만 입력, 단위: %)
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                <div>앱: </div>
                <Input
                  type="number"
                  style={{ width: '200px' }}
                  placeholder="수수료율을 입력하세요"
                  value={createCommissionRateTemplateData.channelCommissionRate?.app?.ratePercent}
                  onChange={(e) => {
                    setCreateCommissionRateTemplateData((prev) => {
                      const newChannelCommissionRate = { ...prev.channelCommissionRate };
                      newChannelCommissionRate['app']!.ratePercent = Number(e.target.value);
                      return { ...prev, channelCommissionRate: newChannelCommissionRate };
                    });
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                <div>네이버: </div>
                <Input
                  type="number"
                  style={{ width: '200px' }}
                  placeholder="수수료율을 입력하세요"
                  value={createCommissionRateTemplateData.channelCommissionRate?.['naver-store']?.ratePercent}
                  onChange={(e) => {
                    setCreateCommissionRateTemplateData((prev) => {
                      const newChannelCommissionRate = { ...prev.channelCommissionRate };
                      newChannelCommissionRate['naver-store']!.ratePercent = Number(e.target.value);
                      return { ...prev, channelCommissionRate: newChannelCommissionRate };
                    });
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                <div>카카오: </div>
                <Input
                  type="number"
                  style={{ width: '200px' }}
                  placeholder="수수료율을 입력하세요"
                  value={createCommissionRateTemplateData.channelCommissionRate?.['kakao-reservation']?.ratePercent}
                  onChange={(e) => {
                    setCreateCommissionRateTemplateData((prev) => {
                      const newChannelCommissionRate = { ...prev.channelCommissionRate };
                      newChannelCommissionRate['kakao-reservation']!.ratePercent = Number(e.target.value);
                      return { ...prev, channelCommissionRate: newChannelCommissionRate };
                    });
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                <div>베네피아: </div>
                <Input
                  type="number"
                  style={{ width: '200px' }}
                  placeholder="수수료율을 입력하세요"
                  value={createCommissionRateTemplateData.channelCommissionRate?.['benepia']?.ratePercent}
                  onChange={(e) => {
                    setCreateCommissionRateTemplateData((prev) => {
                      const newChannelCommissionRate = { ...prev.channelCommissionRate };
                      newChannelCommissionRate['benepia']!.ratePercent = Number(e.target.value);
                      return { ...prev, channelCommissionRate: newChannelCommissionRate };
                    });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </OverlayScrollbarsComponent>
    </Modal>
  );
};
